import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { AdditionalCostCategoryModel } from '../../content/after-sales/model/additional-cost-category-model';
import { AdditionalCostModel } from '../../content/after-sales/model/additional-cost-model';
import { ErrorCostRateModel } from '../../content/after-sales/model/after-sales-error-cost-rate-model';
import { AfterSalesModel } from '../../content/after-sales/model/after-sales-model';
import { completeEndpoint, EndpointPart } from '../../core/global';
import { EndpointService } from '../endpoint/endpoint.service';

@Injectable({
  providedIn: 'root',
})
export class AfterSalesService {
  private readonly http = inject(HttpClient);
  private readonly endpointService = inject(EndpointService);

  public getAfterSalesTabData(): Observable<AfterSalesModel[]> {
    const url: string = this.endpointService.getEndpointUrl(completeEndpoint(EndpointPart.GET_AFTER_SALES));
    return this.http.get<AfterSalesModel[]>(url);
  }

  public getAfterSalesDetails(auditReportId: string): Observable<AfterSalesModel> {
    const pathParams: Map<string, string> = new Map();
    pathParams.set('auditReportId', auditReportId);

    const url: string = this.endpointService.getEndpointUrl(
      completeEndpoint(EndpointPart.GET_AFTER_SALES_DETAILS),
      pathParams,
    );
    return this.http.get<AfterSalesModel>(url);
  }

  public getAdditionalCostCategoryList(): Observable<AdditionalCostCategoryModel> {
    const url: string = this.endpointService.getEndpointUrl(
      completeEndpoint(EndpointPart.GET_ADDITIONAL_COST_CATEGORY_LIST),
    );
    return this.http.get<AdditionalCostCategoryModel>(url);
  }

  public saveAdditionalCost(afterSalesAdditionalCostModel: AdditionalCostModel): Observable<any> {
    return afterSalesAdditionalCostModel.additionalCostsId === null
      ? this.saveNewAdditionalCost(afterSalesAdditionalCostModel)
      : this.updateAdditionalCost(afterSalesAdditionalCostModel);
  }

  private saveNewAdditionalCost(afterSalesAdditionalCostModel: AdditionalCostModel): Observable<any> {
    const url: string = this.endpointService.getEndpointUrl(completeEndpoint(EndpointPart.POST_NEW_ADDITIONAL_COST));
    return this.http.post<AdditionalCostModel>(url, afterSalesAdditionalCostModel);
  }

  public updateAdditionalCost(afterSalesAdditionalCostModel: AdditionalCostModel): Observable<any> {
    const pathParams: Map<string, number> = new Map();
    pathParams.set('additionalCostId', afterSalesAdditionalCostModel.additionalCostsId);

    const url: string = this.endpointService.getEndpointUrl(
      completeEndpoint(EndpointPart.PUT_UPDATE_ADDITIONAL_COST),
      pathParams,
    );
    return this.http.put<AdditionalCostModel>(url, afterSalesAdditionalCostModel);
  }

  public deleteAdditionalCost(additionalCostId: number): Observable<any> {
    const pathParams: Map<string, number> = new Map();
    pathParams.set('additionalCostId', additionalCostId);

    const url: string = this.endpointService.getEndpointUrl(
      completeEndpoint(EndpointPart.DELETE_ADDITIONAL_COST),
      pathParams,
    );
    return this.http.delete<any>(url);
  }

  public getErrorCostRateTableData(): Observable<ErrorCostRateModel[]> {
    const url: string = this.endpointService.getEndpointUrl(completeEndpoint(EndpointPart.GET_ERROR_COST_RATES));
    return this.http.get<ErrorCostRateModel[]>(url);
  }

  public saveErrorCostRate(errorCostRateModel: ErrorCostRateModel, update: boolean): Observable<any> {
    return update ? this.updateErrorCostRate(errorCostRateModel) : this.saveNewErrorCostRate(errorCostRateModel);
  }

  public saveNewErrorCostRate(errorCostRateModel: ErrorCostRateModel): Observable<any> {
    const url: string = this.endpointService.getEndpointUrl(completeEndpoint(EndpointPart.POST_ERROR_COST_RATE));
    return this.http.post<ErrorCostRateModel>(url, errorCostRateModel);
  }

  public updateErrorCostRate(errorCostRateModel: ErrorCostRateModel): Observable<any> {
    const url: string = this.endpointService.getEndpointUrl(completeEndpoint(EndpointPart.PUT_ERROR_COST_RATE));
    return this.http.put<ErrorCostRateModel>(url, errorCostRateModel);
  }
}
